import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { Tables } from '../types/database.types';

export type TenantBranding = Tables<'tenant_branding'>;

export interface SidebarColors {
  bg: string;
  bgDeep: string;
  text: string;
  textMuted: string;
  activeBg: string;
  border: string;
  logoText: string;
}

export function resolveSidebarColors(branding: TenantBranding): SidebarColors {
  const { sidebar_style, color_primary } = branding;

  // Explicit overrides take priority
  if (branding.color_sidebar_bg) {
    return {
      bg: branding.color_sidebar_bg,
      bgDeep: branding.color_sidebar_bg,
      text: branding.color_sidebar_text ?? '#1a1a1a',
      textMuted: branding.color_sidebar_text_muted ?? '#6b7280',
      activeBg: branding.color_sidebar_active_bg ?? `${color_primary}15`,
      border: branding.color_sidebar_border ?? 'rgba(0,0,0,0.08)',
      logoText: branding.color_sidebar_text ?? color_primary,
    };
  }

  // Derive from sidebar_style
  switch (sidebar_style) {
    case 'dark':
      return {
        bg: color_primary,
        bgDeep: '#02241c',
        text: '#ffffff',
        textMuted: 'rgba(255,255,255,0.72)',
        activeBg: 'rgba(255,255,255,0.07)',
        border: 'rgba(255,255,255,0.09)',
        logoText: '#ffffff',
      };
    case 'tinted':
      return {
        bg: `color-mix(in srgb, ${color_primary} 8%, #ffffff)`,
        bgDeep: `color-mix(in srgb, ${color_primary} 8%, #ffffff)`,
        text: '#1a1a1a',
        textMuted: '#5a6b63',
        activeBg: `color-mix(in srgb, ${color_primary} 18%, #ffffff)`,
        border: `color-mix(in srgb, ${color_primary} 15%, #ffffff)`,
        logoText: color_primary,
      };
    case 'light':
    default:
      return {
        bg: '#ffffff',
        bgDeep: '#ffffff',
        text: '#1a1a1a',
        textMuted: '#6b7280',
        activeBg: `${color_primary}14`,
        border: 'rgba(0,0,0,0.08)',
        logoText: color_primary,
      };
  }
}

const DEFAULT_BRANDING: TenantBranding = {
  id: 'default',
  tenant_slug: 'cime',
  firm_name: 'CIME Avocats',
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  color_primary: '#043429',
  color_accent: '#718063',
  color_gold: '#c9a84c',
  color_bg: '#f6f4ee',
  color_surface: '#ffffff',
  font_heading: 'Playfair Display',
  font_body: 'Inter',
  font_heading_url: null,
  font_body_url: null,
  border_radius: '8px',
  sidebar_style: 'dark',
  color_sidebar_bg: null,
  color_sidebar_text: null,
  color_sidebar_text_muted: null,
  color_sidebar_active_bg: null,
  color_sidebar_border: null,
  color_text_primary: '#1f2823',
  color_text_muted: '#5c6660',
  color_danger: '#dc2626',
  color_success: '#16a34a',
  color_warning: '#d97706',
  theme_preset: 'cime_classic',
  is_active: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

interface ThemeContextValue {
  branding: TenantBranding;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  branding: DEFAULT_BRANDING,
  isLoading: false,
});

function injectFont(url: string): void {
  if (!url) return;
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

function applyThemeToDocument(branding: TenantBranding): void {
  const root = document.documentElement;

  // Core brand vars
  root.style.setProperty('--cime-primary', branding.color_primary);
  root.style.setProperty('--cime-accent', branding.color_accent);
  root.style.setProperty('--cime-gold', branding.color_gold);
  root.style.setProperty('--cime-bg', branding.color_bg);
  root.style.setProperty('--cime-surface', branding.color_surface);
  root.style.setProperty(
    '--cime-font-heading',
    `'${branding.font_heading}', Georgia, serif`,
  );
  root.style.setProperty(
    '--cime-font-body',
    `'${branding.font_body}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  );
  root.style.setProperty('--cime-radius', branding.border_radius);

  // Sidebar vars (resolved)
  const sidebar = resolveSidebarColors(branding);
  root.style.setProperty('--cime-sidebar-bg', sidebar.bg);
  root.style.setProperty('--cime-sidebar-bg-deep', sidebar.bgDeep);
  root.style.setProperty('--cime-sidebar-text', sidebar.text);
  root.style.setProperty('--cime-sidebar-text-muted', sidebar.textMuted);
  root.style.setProperty('--cime-sidebar-active-bg', sidebar.activeBg);
  root.style.setProperty('--cime-sidebar-border', sidebar.border);
  root.style.setProperty('--cime-sidebar-logo-text', sidebar.logoText);

  // Semantic color vars
  root.style.setProperty('--cime-text-primary', branding.color_text_primary);
  root.style.setProperty('--cime-text-muted', branding.color_text_muted);
  root.style.setProperty('--cime-danger', branding.color_danger);
  root.style.setProperty('--cime-success', branding.color_success);
  root.style.setProperty('--cime-warning', branding.color_warning);

  if (branding.font_heading_url) {
    injectFont(branding.font_heading_url);
  }
  if (branding.font_body_url) {
    injectFont(branding.font_body_url);
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['branding', 'cime'],
    queryFn: async (): Promise<TenantBranding> => {
      const { data, error } = await supabase
        .from('tenant_branding')
        .select('*')
        .eq('tenant_slug', 'cime')
        .eq('is_active', true)
        .single();

      if (error || !data) {
        return DEFAULT_BRANDING;
      }
      return data;
    },
    staleTime: 1000 * 60 * 10,
    retry: false,
  });

  const branding = data ?? DEFAULT_BRANDING;

  useEffect(() => {
    applyThemeToDocument(branding);
  }, [branding]);

  return (
    <ThemeContext.Provider value={{ branding, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTenantBranding(): ThemeContextValue {
  return useContext(ThemeContext);
}
